import { useState } from "react";
import { toast } from "sonner";
import api, { getApiErrorMessage } from "@/lib/api";
import { Users, X, Loader2, UserMinus } from "lucide-react";

/**
 * Floating bulk action bar for the leads board.
 * Visible only to admin/office_manager when 1+ leads are selected.
 * Assigns (or unassigns) many leads at once via /api/leads/batch/assign.
 */
export function BulkActionBar({ selectedIds, users, canAssign, onDone, onClear }) {
  const [repId, setRepId] = useState("");
  const [running, setRunning] = useState(false);

  const count = selectedIds.length;
  if (count === 0 || !canAssign) return null;

  const reps = (users || []).filter((u) => u.role === "rep" && u.active);

  const doAssign = async (targetRepId) => {
    if (running) return;
    if (targetRepId === "") {
      toast.error("Pick a rep first, or use Unassign.");
      return;
    }
    setRunning(true);
    try {
      const { data } = await api.post("/leads/batch/assign", {
        place_ids: selectedIds,
        rep_id: targetRepId || null,
      });
      const verb = data.rep_name ? `assigned to ${data.rep_name}` : "unassigned";
      const bits = [`${data.assigned} lead${data.assigned === 1 ? "" : "s"} ${verb}`];
      if (data.blocked?.length) bits.push(`${data.blocked.length} blocked (order signed/paid)`);
      if (data.missing?.length) bits.push(`${data.missing.length} not found`);
      toast.success(bits.join(" · "));
      onDone && onDone();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-4 py-3 shadow-2xl"
      style={{
        background: "linear-gradient(90deg, rgba(34,211,192,0.15) 0%, rgba(14,19,21,0.98) 60%, rgba(14,19,21,0.98) 100%)",
        border: "1px solid rgba(34,211,192,0.5)",
        backdropFilter: "blur(12px)",
        borderRadius: "6px",
        maxWidth: "calc(100vw - 3rem)",
      }}
      data-testid="bulk-action-bar"
    >
      <div className="flex items-center gap-2 pr-3 border-r border-line">
        <Users size={14} className="text-cyan" />
        <span className="text-sm font-medium text-ink whitespace-nowrap" data-testid="bulk-count">
          {count} selected
        </span>
      </div>

      <label className="flex items-center gap-2 text-xs">
        <span className="text-ink-muted font-mono uppercase tracking-wider text-[10px] whitespace-nowrap">
          Assign to
        </span>
        <select
          value={repId}
          onChange={(e) => setRepId(e.target.value)}
          disabled={running}
          data-testid="bulk-assign-select"
          className="bg-bg border border-line rounded-sm px-2 py-1.5 text-sm text-ink focus:border-cyan transition-colors"
        >
          <option value="" className="bg-bg-surface">Pick a rep…</option>
          {reps.map((r) => (
            <option key={r.id} value={r.id} className="bg-bg-surface">
              {r.name}
            </option>
          ))}
        </select>
      </label>

      <button
        onClick={() => doAssign(repId)}
        disabled={running || !repId}
        data-testid="bulk-assign-btn"
        className="flex items-center gap-1.5 text-sm bg-cyan text-bg font-medium px-3 py-1.5 rounded-sm hover:bg-cyan-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {running ? <Loader2 size={13} className="animate-spin" /> : <Users size={13} />}
        Assign
      </button>

      <div className="w-px h-6 bg-line" />

      <button
        onClick={() => doAssign(null)}
        disabled={running}
        data-testid="bulk-unassign-btn"
        className="flex items-center gap-1.5 text-sm border border-line text-ink-secondary hover:text-ink hover:bg-bg-hover px-3 py-1.5 rounded-sm transition-colors disabled:opacity-40"
      >
        <UserMinus size={13} />
        Unassign
      </button>

      <button
        onClick={onClear}
        disabled={running}
        data-testid="bulk-clear-btn"
        className="text-ink-muted hover:text-ink transition-colors ml-1"
        aria-label="Clear selection"
      >
        <X size={14} />
      </button>
    </div>
  );
}
